"use client";

import * as React from "react"
import { Input as InputPrimitive } from "@base-ui/react/input"

import { cn } from "@/lib/utils"

export const passwordManagerOptOutProps = {
  "data-1p-ignore": "",
  "data-lpignore": "true",
  "data-bwignore": "",
  "data-form-type": "other",
} as const

export type InputProps = React.ComponentProps<"input"> & {

  sanitizeValue?: (value: string) => string
}

function Input({
  className,
  type,
  autoComplete,
  sanitizeValue,
  onInput,
  onChange,
  onCompositionEnd,
  onFocus,
  onBlur,
  value,
  defaultValue,
  ...props
}: InputProps) {
  const resolvedAutoComplete = autoComplete ?? "off"
  const sanitize = (input: HTMLInputElement) => {
    if (!sanitizeValue || input.readOnly || input.disabled) return
    const raw = input.value
    const next = sanitizeValue(raw)
    if (next === raw) return
    const start = input.selectionStart
    const end = input.selectionEnd
    const direction = input.selectionDirection
    input.value = next
    if (start !== null && end !== null) {
      try { input.setSelectionRange(sanitizeValue(raw.slice(0, start)).length, sanitizeValue(raw.slice(0, end)).length, direction ?? undefined) }
      catch { /* email / number 原生类型不提供文本选区接口 */ }
    }
  }
  const clean = (next: InputProps["value"]) => typeof next === "string" && sanitizeValue ? sanitizeValue(next) : next

  return (
    <InputPrimitive
      type={type}
      value={clean(value)}
      defaultValue={clean(defaultValue)}
      onInput={sanitizeValue ? event => { sanitize(event.currentTarget); onInput?.(event) } : onInput}
      onChange={sanitizeValue ? event => { sanitize(event.currentTarget); onChange?.(event) } : onChange}
      onCompositionEnd={sanitizeValue ? event => { sanitize(event.currentTarget); onCompositionEnd?.(event) } : onCompositionEnd}
      onFocus={sanitizeValue ? event => { sanitize(event.currentTarget); onFocus?.(event) } : onFocus}
      onBlur={sanitizeValue ? event => { sanitize(event.currentTarget); onBlur?.(event) } : onBlur}
      autoComplete={resolvedAutoComplete}
      {...(resolvedAutoComplete === "off" ? passwordManagerOptOutProps : null)}
      data-slot="input"
      className={cn(
        "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors outline-none file:inline-flex file:h-6 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:border-[#333] focus-visible:ring-0 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
        className
      )}
      {...props}
    />
  )
}

export { Input }
